import { useMemo, useState, type ReactNode } from "react";
import type { Dayjs } from "dayjs";

import { SIDER_TABS } from "../../../utils/Constants";
import { getColorBasedOnValue } from "../../../utils/colorUtils";
import { buildUserManagerUrl } from "../../../utils/NavigationUtils";
import "../../ReportingSummary/stylesReportingSummary.css";
import TopUsersPage from "../../TopUsers/TopUsers";
import type {
  TopUsersAppliedParams,
  TopUsersColumn,
  TopUsersFilterField,
  TopUsersKpiTile,
  TopUsersSelectOption,
  TopUsersViewOption,
} from "../../TopUsers/TopUsers";
import {
  useGetSportsbookTopUsers,
  useGetSportsbookTopUsersKpis,
  type SportsbookTopUserRow,
  type SportsbookTopUsersFilterDefinition,
  type SportsbookTopUsersFilters,
  type SportsbookTopUsersKpisData,
  type SportsbookTopUsersView,
} from "../../../queries/sportsbook-kpis/useGetSportsbookTopUsers";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { formatCurrency } from "../../../utils/financialFormatters";
import { getAdminNow } from "../../../utils/dateService";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import styles from "./TopUsers.module.css";

// ─── Static fallbacks ────────────────────────────────────────────────────────
//
// `meta.filters` from the table response is the source of truth — these
// static lists only render on the first frame (before the BE response
// arrives) so the dropdowns aren't empty.

const DEFAULT_LIMIT = 20;

const VIEW_OPTIONS: ReadonlyArray<TopUsersViewOption> = [
  { id: "top_winners", label: "Top Winners" },
  { id: "top_losers", label: "Top Losers" },
  { id: "top_turnover", label: "Top Turnover" },
];

const FALLBACK_LIMIT_OPTIONS: ReadonlyArray<TopUsersSelectOption<number>> = [
  { value: 10, label: "10 Users" },
  { value: 20, label: "20 Users" },
  { value: 50, label: "50 Users" },
  { value: 100, label: "100 Users" },
];

const FALLBACK_SPORT_OPTIONS: ReadonlyArray<TopUsersSelectOption<string>> = [
  { value: "nba", label: "NBA" },
  { value: "nfl", label: "NFL" },
  { value: "mlb", label: "MLB" },
  { value: "nhl", label: "NHL" },
  { value: "soccer", label: "Soccer" },
];

const FALLBACK_STATE_OPTIONS: ReadonlyArray<TopUsersSelectOption<string>> = [
  { value: "CA", label: "California" },
  { value: "NJ", label: "New Jersey" },
  { value: "NY", label: "New York" },
  { value: "PA", label: "Pennsylvania" },
];

// ─── Formatters ──────────────────────────────────────────────────────────────

const formatPercentage = (value: number, fractionDigits = 0) =>
  `${Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}%`;

/**
 * Wraps a numeric cell value in a `<span>` whose text colour reflects
 * its sign — green for non-negative, red for negative — using the
 * shared `getColorBasedOnValue` helper (`var(--green-400)` /
 * `var(--red-400)`). Used for P&L and Margin % so a Top Loser table
 * row reads at a glance as red even before the operator parses the
 * number.
 *
 * Null / undefined / non-finite values are treated as `0` (neutral
 * green) — the parser already coerces missing fields to `0`, so this
 * is mostly a defensive fallback against unexpected BE shapes.
 */
const renderColoredNumber = (
  value: unknown,
  formatter: (n: number) => string,
): ReactNode => {
  const numeric =
    typeof value === "number" && Number.isFinite(value) ? value : 0;
  return (
    // Stable testid so tests can grab the inner span without walking
    // the DOM (which would trip `testing-library/no-node-access`).
    // The testid is intentionally column-agnostic — duplicates across
    // rows/columns are resolved by scoping queries with `within(cell)`.
    <span
      style={{ color: getColorBasedOnValue(numeric) }}
      data-testid="top-users-colored-value"
    >
      {formatter(numeric)}
    </span>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ISO_DATE_FORMAT = "YYYY-MM-DD";

type StringFilterOptions = ReadonlyArray<TopUsersSelectOption<string>>;

type DateRange = [Dayjs, Dayjs];

/**
 * Dates live OUTSIDE `AppliedFilters` so they commit immediately on date
 * picker change (mirroring `AllManualActions` — the date prop flows
 * straight into the query keys and React Query refetches on its own,
 * without an Apply click). `limit`/`sport`/`state` stay draft-only and
 * only commit through `onApply`.
 */
type AppliedFilters = {
  limit: number;
  sport?: string;
  state?: string;
};

const buildInitialDates = (): DateRange => {
  const now = getAdminNow();
  return [now.startOf("month"), now.endOf("day")];
};

const buildInitialAppliedFilters = (): AppliedFilters => ({
  limit: DEFAULT_LIMIT,
  sport: undefined,
  state: undefined,
});

const findFilterDefinition = (
  filters: ReadonlyArray<SportsbookTopUsersFilterDefinition>,
  id: string,
): SportsbookTopUsersFilterDefinition | undefined =>
  filters.find((entry) => entry.id === id);

/**
 * Tooltip copy rendered behind the "i" icon at the top-right of every
 * non-rate KPI tile (TURNOVER, P&L, BETS). MARGIN % is a rate so the
 * badge is always hidden and no tooltip applies.
 *
 * Wording is intentionally fixed by the product team and kept as
 * "percent change" even though the underlying badge value now reads
 * `pctOfTotal` — do not reword without checking with them first.
 */
const buildPercentChangeTooltip = (metricName: string): string =>
  `The percent change metric compares the ${metricName} for the top user against the ${metricName} for all users with respect to the time filter. This shows how the Top Users affect this metric for the entire application.`;

const toStringOptions = (
  definition: SportsbookTopUsersFilterDefinition | undefined,
  fallback: StringFilterOptions,
): StringFilterOptions => {
  if (!definition?.options.length) return fallback;
  return definition.options.map((opt) => ({
    value: String(opt.value),
    label: opt.label,
  }));
};

// Static label maps — BE returns the raw id / numeric value as the
// "label" (e.g. `"top_winners"`, `"20"`), so the FE owns the
// human-readable strings. BE drives the *set* of available options;
// the FE drives how each option reads.

const VIEW_LABEL_BY_ID = new Map(VIEW_OPTIONS.map((v) => [v.id, v.label]));

const LIMIT_LABEL_BY_VALUE = new Map(
  FALLBACK_LIMIT_OPTIONS.map((opt) => [opt.value, opt.label]),
);

const toLimitOptions = (
  definition: SportsbookTopUsersFilterDefinition | undefined,
): ReadonlyArray<TopUsersSelectOption<number>> => {
  if (!definition?.options.length) return FALLBACK_LIMIT_OPTIONS;
  // BE may surface limit options as `"25"` or `25`; normalise to number so
  // the shared `Select<number>` round-trips the value correctly. The
  // human label ("20 Users") comes from the static map — falling back
  // to the BE-provided string ("20") only for sizes we haven't pre-
  // labelled, so a new BE-driven size still renders.
  return definition.options
    .map((opt) => {
      const numeric = Number(opt.value);
      if (!Number.isFinite(numeric)) return null;
      return {
        value: numeric,
        label: LIMIT_LABEL_BY_VALUE.get(numeric) ?? opt.label,
      };
    })
    .filter((opt): opt is TopUsersSelectOption<number> => opt !== null);
};

const toViewOptions = (
  definition: SportsbookTopUsersFilterDefinition | undefined,
): ReadonlyArray<TopUsersViewOption> => {
  if (!definition?.options.length) return VIEW_OPTIONS;
  // Preserve the static "Top Winners"/"Top Losers"/"Top Turnover" labels
  // — the BE returns the snake-case id as the label (e.g.
  // `"top_winners"`), which is fine for the wire but reads badly as a
  // tab. Unknown ids surfaced by a future BE change fall through to the
  // raw BE label so the new tab still appears.
  return definition.options.map((opt) => {
    const id = String(opt.value);
    return {
      id,
      label: VIEW_LABEL_BY_ID.get(id) ?? opt.label,
    };
  });
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Sportsbook → Reporting → Top Users.
 *
 * Thin wrapper around the shared `TopUsers` page (`src/components/TopUsers`).
 * Owns the Sportsbook-specific concerns:
 *   • the three view tabs (`Top Winners`, `Top Losers`, `Top Turnover`),
 *   • the `Sport` and `State` single-select filters,
 *   • the KPI tile titles and table columns,
 *   • the data fetching (`/v2/sportsbook/analytics/top-users` and its
 *     `/kpis` sibling, both gated by `sportsbook.reporting.top_users`).
 *
 * Filter / limit / view option lists prefer the BE-driven `meta.filters`
 * definitions from the table response and fall back to static lists on
 * the first render (before the BE payload arrives).
 *
 * Commit semantics:
 *   • `dates`     → refetch immediately on change (`onDatesChange`).
 *   • `view` tab  → refetch immediately on click (`onViewChange`).
 *   • `limit`/`sport`/`state` → draft inside the shared component;
 *     commit only on Apply (mirrors the UtmReports / `AllManualActions`
 *     pattern where dates are immediate and panel filters are deferred).
 */
const TopUsers = () => {
  usePageTitle(AppStrings.TOP_USERS);
  const [activeView, setActiveView] =
    useState<SportsbookTopUsersView>("top_winners");
  const [dates, setDates] = useState<DateRange>(buildInitialDates);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(
    buildInitialAppliedFilters,
  );

  const currencySymbol = useCurrencySymbol();

  // Shared query filters — both the KPIs and the table hook use the same
  // payload so they stay aligned per the BE contract (KPIs are the top-N
  // aggregate for the *current* view + filters; rows are the same set).
  // `start_date`/`end_date` are derived from `dates` directly so the
  // queries re-key (and refetch) the moment the date picker changes.
  const queryFilters: SportsbookTopUsersFilters = useMemo(
    () => ({
      start_date: dates[0].format(ISO_DATE_FORMAT),
      end_date: dates[1].format(ISO_DATE_FORMAT),
      limit: appliedFilters.limit,
      view: activeView,
      sport: appliedFilters.sport,
      state: appliedFilters.state,
    }),
    [activeView, dates, appliedFilters],
  );

  const tableQuery = useGetSportsbookTopUsers(queryFilters);
  const kpisQuery = useGetSportsbookTopUsersKpis(queryFilters);

  // Surface request failures as a banner above the toolbar — without
  // this, a 4xx/5xx silently renders an empty table + zero-value KPI
  // tiles, leaving the operator unable to tell "no data" from
  // "request failed". Copy attributes the failure to the affected
  // endpoint so the operator knows whether to retry, refresh, or
  // escalate.
  const errorMessage = (() => {
    if (tableQuery.isError && kpisQuery.isError) {
      return "Failed to load Top Users data. Please try again.";
    }
    if (tableQuery.isError) {
      return "Failed to load the Top Users table. Please try again.";
    }
    if (kpisQuery.isError) {
      return "Failed to load the Top Users KPI tiles. Please try again.";
    }
    return undefined;
  })();

  // ─── Filter definitions (BE-driven where available) ──────────────────────

  const filterDefinitions = useMemo(
    () => tableQuery.data?.filters ?? [],
    [tableQuery.data?.filters],
  );

  const limitOptions = useMemo(
    () => toLimitOptions(findFilterDefinition(filterDefinitions, "limit")),
    [filterDefinitions],
  );

  const viewTabs = useMemo(
    () => toViewOptions(findFilterDefinition(filterDefinitions, "view")),
    [filterDefinitions],
  );

  const sportOptions = useMemo(
    () =>
      toStringOptions(
        findFilterDefinition(filterDefinitions, "sport"),
        FALLBACK_SPORT_OPTIONS,
      ),
    [filterDefinitions],
  );

  const stateOptions = useMemo(
    () =>
      toStringOptions(
        findFilterDefinition(filterDefinitions, "state"),
        FALLBACK_STATE_OPTIONS,
      ),
    [filterDefinitions],
  );

  const filters = useMemo<ReadonlyArray<TopUsersFilterField>>(
    () => [
      {
        id: "sport",
        placeholder: "Sport",
        options: [...sportOptions],
        allowClear: true,
        testId: "top-users-sport-select",
      },
      {
        id: "state",
        placeholder: "State",
        options: [...stateOptions],
        allowClear: true,
        testId: "top-users-state-select",
      },
    ],
    [sportOptions, stateOptions],
  );

  // ─── KPI tiles ───────────────────────────────────────────────────────────
  //
  // BE returns `turnover`, `pnl`, `margin_pct`, and `bets` per the
  // current Sportsbook contract (any entry can come back as `null`,
  // which the parser collapses to the zero-value `EMPTY_KPI` so this
  // code can dereference safely). All four tiles are data-bound.
  //
  // Badge source: every tile reads `pctOfTotal` (share-of-total —
  // "how concentrated is this metric in the top-N"), per product.
  // MARGIN % surfaces `null` (a rate has no meaningful
  // share-of-total) — the wrapper coerces `null → undefined` so
  // `KpiCard` hides the badge instead of rendering "0%".
  //
  // `percentageMaximumFractionDigits: 2` is pinned on every tile that
  // can surface a badge so a sub-1% share (e.g. `0.07`) survives the
  // integer-precision tiles (BETS sets `maximumFractionDigits: 0` on
  // the main value) without rounding down to `"0%"`.
  //
  // Diverges from `Casino/TopUsers`, which still surfaces
  // `percentChange` on every tile.

  const kpis = useMemo<ReadonlyArray<TopUsersKpiTile>>(() => {
    const data: SportsbookTopUsersKpisData | undefined = kpisQuery.data;
    const turnover = data?.turnover;
    const pnl = data?.pnl;
    const marginPct = data?.margin_pct;
    const bets = data?.bets;
    return [
      {
        key: "turnover",
        title: "TURNOVER",
        titleTooltip: buildPercentChangeTooltip("turnover"),
        value: turnover?.value ?? 0,
        percentage: turnover?.pctOfTotal ?? undefined,
        currencySymbol,
        maximumFractionDigits: 2,
        percentageMaximumFractionDigits: 2,
      },
      {
        key: "pnl",
        title: "PROFIT & LOSS",
        titleTooltip: buildPercentChangeTooltip("profit & loss"),
        value: pnl?.value ?? 0,
        percentage: pnl?.pctOfTotal ?? undefined,
        currencySymbol,
        maximumFractionDigits: 2,
        percentageMaximumFractionDigits: 2,
      },
      {
        key: "margin_pct",
        title: "MARGIN %",
        // Intentionally no `titleTooltip` — a rate has no meaningful
        // share-of-total signal, so the badge is always hidden.
        value: marginPct?.value ?? 0,
        percentage: marginPct?.pctOfTotal ?? undefined,
        rate: true,
        maximumFractionDigits: 2,
      },
      {
        key: "bets",
        title: "BETS",
        titleTooltip: buildPercentChangeTooltip("bets"),
        value: bets?.value ?? 0,
        percentage: bets?.pctOfTotal ?? undefined,
        maximumFractionDigits: 0,
        percentageMaximumFractionDigits: 2,
      },
    ];
  }, [currencySymbol, kpisQuery.data]);

  // ─── Table ───────────────────────────────────────────────────────────────

  // Sort icons are intentionally omitted on every column — the rows are
  // already returned sorted by the BE per the active `view` (Top
  // Winners / Top Losers / Top Turnover), so a per-column resort would
  // contradict the tab's semantics ("top by what?"). Dropping `sorter`
  // tells antd not to render the sort affordance at all.
  const columns = useMemo<ReadonlyArray<TopUsersColumn<SportsbookTopUserRow>>>(
    () => {
      const money = (value: number) =>
        formatCurrency(Number(value ?? 0), currencySymbol, 2);

      return [
      {
        title: "Username",
        dataIndex: "username",
        key: "username",
        width: 160,
        // Username always links to User Overview by `user_id` per the BE
        // contract — even when `username` is null the cell stays clickable
        // (the label falls back to `userId` in the parser).
        render: (_value, row) => (
          <a
            href={buildUserManagerUrl(row.userId)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.usernameLink}
            data-testid={`top-users-username-link-${row.userId}`}
          >
            {row.username || row.userId}
          </a>
        ),
      },
      {
        title: "User ID",
        dataIndex: "userId",
        key: "userId",
        width: 120,
      },
      {
        title: "Turnover",
        dataIndex: "turnover",
        key: "turnover",
        width: 140,
        // Two decimals to stay aligned with the Turnover / P&L KPI tiles
        // above the table (`maximumFractionDigits: 2`) — without this the
        // same currency reads as `$13,750` in the cell and `$13,750.00`
        // in the tile, which operators consistently flagged as broken.
        render: (value) => money(value as number),
      },
      {
        title: "P&L",
        dataIndex: "pnl",
        key: "pnl",
        width: 140,
        // Coloured by sign so Top Losers rows read as red at a glance.
        // Turnover stays the default colour — it can only be ≥ 0.
        render: (value) => renderColoredNumber(value, (n) => money(n)),
      },
      {
        title: "Margin %",
        dataIndex: "marginPct",
        key: "marginPct",
        width: 140,
        render: (value) =>
          renderColoredNumber(value, (n) => formatPercentage(n, 2)),
      },
    ];
    },
    [currencySymbol],
  );

  // Apply only commits the deferred filters (`limit`/`sport`/`state`).
  // Dates were already committed via `onDatesChange`, so `params.dates`
  // is intentionally ignored — re-committing here would just produce a
  // no-op `setDates` call with the same value.
  const handleApply = (params: TopUsersAppliedParams) => {
    const limit = (params.filters.limit as number | undefined) ?? DEFAULT_LIMIT;
    setAppliedFilters({
      limit,
      sport: (params.filters.sport as string | undefined) || undefined,
      state: (params.filters.state as string | undefined) || undefined,
    });
  };

  // Drives the Clear Filters button visibility (it stays hidden until at
  // least one filter is actually narrowing the query — same semantic as
  // `UtmReports.hasAppliedFilters`). Keyed off *applied* state, not the
  // draft, so an unapplied draft selection doesn't surface a button
  // that has nothing to clear yet. The view tab and the date range are
  // intentionally excluded — they're navigation / primary controls,
  // not filters.
  const hasAppliedFilters =
    appliedFilters.limit !== DEFAULT_LIMIT ||
    appliedFilters.sport !== undefined ||
    appliedFilters.state !== undefined;

  // Mirrors the shared component's draft reset on the wrapper's applied
  // side. The shared component has already wiped its draft dropdowns
  // by the time this fires.
  const handleClear = () => {
    setAppliedFilters(buildInitialAppliedFilters());
  };

  return (
    <TopUsersPage<SportsbookTopUserRow>
      product={SIDER_TABS?.SPORTSBOOK}
      hideTitle
      showDatePicker
      initialDates={dates}
      onDatesChange={setDates}
      views={viewTabs}
      activeView={activeView}
      onViewChange={(id) => setActiveView(id as SportsbookTopUsersView)}
      filters={filters}
      limitOptions={limitOptions}
      defaultLimit={DEFAULT_LIMIT}
      onApply={handleApply}
      onClear={handleClear}
      showClear={hasAppliedFilters}
      kpis={kpis}
      // Keep the two loading states independent so the table and the
      // KPI row don't blink together when only one of the queries is
      // in flight (they have separate cache keys + endpoints).
      kpisLoading={kpisQuery.isFetching}
      columns={columns}
      rows={tableQuery.data?.rows ?? []}
      loading={tableQuery.isFetching}
      errorMessage={errorMessage}
      // Match the Reporting Summary table skin verbatim — same skin
      // class on the antd `<Table>` + zebra-striped row class on
      // every other row. Combined with the side-effect CSS import
      // above, this is what makes the two tables visually identical.
      tableClassName="reporting-summary-table"
      rowClassName={(_record: SportsbookTopUserRow, index: number) =>
        index % 2 === 1 ? "reporting-summary-table-row--alt" : ""
      }
    />
  );
};

export default TopUsers;
